export interface UploadEntry {
    path: string;
    isDirectory: boolean;
    sizeBytes: number;
    version: string;
}

interface UploadManifest {
    entries: UploadEntry[];
    complete: boolean;
}

export interface UploadProgress {
    phase: 'scanning' | 'transferring' | 'completed';
    totalFiles: number;
    totalBytes: number;
    completedBytes: number;
    failedFiles: number;
    failedBytes: number;
    failures: { path: string; message: string }[];
    activeBytes: number;
    elapsedSeconds: number;
    lastActivitySeconds: number;
    bytesPerSecond: number;
    remainingSeconds: number | null;
    downloaded: number;
    skipped: number;
    skippedBytes: number;
    bytes: number;
    currentPath: string;
}

export function emptyUploadProgress(): UploadProgress {
    return {
        phase: 'scanning',
        totalFiles: 0,
        totalBytes: 0,
        completedBytes: 0,
        failedFiles: 0,
        failedBytes: 0,
        failures: [],
        activeBytes: 0,
        elapsedSeconds: 0,
        lastActivitySeconds: 0,
        bytesPerSecond: 0,
        remainingSeconds: null,
        downloaded: 0,
        skipped: 0,
        skippedBytes: 0,
        bytes: 0,
        currentPath: '',
    };
}

interface ManifestPage {
    scan: string;
    page: number;
    entries: UploadEntry[];
}

interface FileCheckpoint {
    destination: string;
    source: string;
    path: string;
    version: string;
    size: number;
    lastModified: number;
}

interface Destination {
    id: string;
    handle: FileSystemDirectoryHandle;
}

export type UploadRequest = (
    endpoint: 'manifest' | 'file' | 'batch',
    params: Record<string, string>,
    signal: AbortSignal,
    method?: 'GET' | 'HEAD' | 'POST',
) => Promise<Response>;

function readRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function saveRecord(
    database: IDBDatabase,
    store: string,
    value: Destination | FileCheckpoint | ManifestPage,
) {
    return new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(store, 'readwrite');
        transaction.objectStore(store).put(value);
        transaction.oncomplete = () => resolve();
        transaction.onabort = () => reject(transaction.error);
        transaction.onerror = () => reject(transaction.error);
    });
}

function openCheckpoints(): Promise<IDBDatabase> {
    const request = indexedDB.open('corridor-uploads-transfer', 2);
    request.onupgradeneeded = event => {
        if (event.oldVersion < 1) {
            request.result.createObjectStore('destinations', { keyPath: 'id' });
            request.result.createObjectStore('files', {
                keyPath: ['destination', 'source', 'path'],
            });
        }
        request.result.createObjectStore('manifest', { keyPath: ['scan', 'page'] });
    };
    return readRequest(request);
}

async function getDestination(database: IDBDatabase, handle: FileSystemDirectoryHandle) {
    const destinations: Destination[] = await readRequest(
        database.transaction('destinations').objectStore('destinations').getAll(),
    );
    for (const destination of destinations) {
        if (await destination.handle.isSameEntry(handle)) return destination.id;
    }
    const id = crypto.randomUUID();
    await saveRecord(database, 'destinations', { id, handle });
    return id;
}

function localNameError(path: string, error: unknown): Error {
    const reason =
        error instanceof TypeError && error.message.includes('Name is not allowed')
            ? 'Your browser does not allow this filename. Retrying the same name will fail.'
            : error instanceof Error
              ? error.message
              : 'Could not create this file or folder in the selected location.';
    return new Error(`${path}: ${reason}`, { cause: error });
}

async function localDirectory(root: FileSystemDirectoryHandle, path: string) {
    let directory = root;
    for (const part of path.split('/').filter(Boolean)) {
        if (part === '.' || part === '..' || part.includes('\\')) {
            throw new Error('Invalid upload path. Contact your administrator.');
        }
        try {
            directory = await directory.getDirectoryHandle(part, { create: true });
        } catch (error) {
            throw localNameError(path, error);
        }
    }
    return directory;
}

interface PreparedFile {
    entry: UploadEntry;
    handle: FileSystemFileHandle;
}

interface BatchRecord {
    index: number;
    data?: string;
    complete?: boolean;
    error?: string;
}

async function* responseRecords<T>(response: Response, signal: AbortSignal): AsyncGenerator<T> {
    if (!response.body) throw new Error('No content returned. Retry the transfer.');
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let pending = '';
    try {
        while (true) {
            signal.throwIfAborted();
            const chunk = await reader.read();
            if (chunk.done) break;
            pending += chunk.value;
            let newline = pending.indexOf('\n');
            while (newline >= 0) {
                signal.throwIfAborted();
                const line = pending.slice(0, newline);
                pending = pending.slice(newline + 1);
                yield JSON.parse(line);
                newline = pending.indexOf('\n');
            }
        }
        if (pending.trim()) throw new Error('Transfer response interrupted. Retry the transfer.');
    } finally {
        await reader.cancel().catch((): void => {});
        reader.releaseLock();
    }
}

/** Scans once into temporary browser storage, then streams files with resumable checkpoints. */
export async function transferUploads(
    root: FileSystemDirectoryHandle,
    source: string,
    request: UploadRequest,
    onProgress: (progress: UploadProgress) => void,
    signal: AbortSignal,
): Promise<void> {
    const database = await openCheckpoints();
    const scan = crypto.randomUUID();
    const controller = new AbortController();
    const cancel = () => controller.abort(signal.reason);
    signal.addEventListener('abort', cancel, { once: true });
    if (signal.aborted) cancel();
    const progress = emptyUploadProgress();
    const started = performance.now();
    let transferStarted = started;
    let lastActivity = started;
    let lastUpdate = 0;
    const update = (force = false, activity = true) => {
        const now = performance.now();
        if (activity) lastActivity = now;
        if (!force && now - lastUpdate < 100) return;
        lastUpdate = now;
        progress.elapsedSeconds = (now - started) / 1000;
        progress.lastActivitySeconds = (now - lastActivity) / 1000;
        const seconds = (now - transferStarted) / 1000;
        const completed = progress.downloaded + progress.skipped + progress.failedFiles;
        const processed = progress.completedBytes + progress.activeBytes + progress.failedBytes;
        progress.bytesPerSecond =
            progress.phase !== 'scanning' && seconds > 0 ? progress.bytes / seconds : 0;
        const bytesRemaining =
            processed > 0
                ? (Math.max(0, progress.totalBytes - processed) * seconds) / processed
                : 0;
        const filesRemaining =
            completed > 0
                ? (Math.max(0, progress.totalFiles - completed) * seconds) / completed
                : 0;
        progress.remainingSeconds =
            progress.phase === 'completed'
                ? 0
                : progress.phase === 'transferring' && (completed > 0 || processed > 0)
                  ? Math.max(bytesRemaining, filesRemaining)
                  : null;
        onProgress({ ...progress, failures: [...progress.failures] });
    };
    const heartbeat = setInterval(() => update(true, false), 1000);
    try {
        const destination = await getDestination(database, root);
        // Persist metadata batches so large manifests never need to stay in memory or be fetched twice.
        const response = await request('manifest', {}, controller.signal);
        let pageCount = 0;
        let complete = false;
        for await (const manifest of responseRecords<UploadManifest>(response, controller.signal)) {
            await saveRecord(database, 'manifest', {
                scan,
                page: pageCount++,
                entries: manifest.entries,
            });
            for (const entry of manifest.entries) {
                if (!entry.isDirectory) {
                    progress.totalFiles++;
                    progress.totalBytes += entry.sizeBytes;
                }
                progress.currentPath = entry.path;
            }
            complete = manifest.complete;
            update(true);
        }
        if (!complete) throw new Error('File scan interrupted. Retry the transfer.');
        async function* pages() {
            for (let page = 0; page < pageCount; page++) {
                controller.signal.throwIfAborted();
                const cached: ManifestPage = await readRequest(
                    database.transaction('manifest').objectStore('manifest').get([scan, page]),
                );
                yield cached.entries;
            }
        }
        progress.phase = 'transferring';
        transferStarted = performance.now();
        update(true);
        const markFailed = (path: string, sizeBytes: number, error: unknown) => {
            progress.failedFiles++;
            progress.failedBytes += sizeBytes;
            // Bound the error list even when thousands of files fail.
            if (progress.failures.length < 100) {
                progress.failures.push({
                    path,
                    message:
                        error instanceof Error
                            ? error.message.replace(`${path}: `, '')
                            : 'Could not save this file.',
                });
            }
            update(true);
        };
        const prepare = async (entry: UploadEntry): Promise<PreparedFile | null> => {
            controller.signal.throwIfAborted();
            progress.currentPath = entry.path;
            update();
            const parts = entry.path.split('/');
            const name = parts.pop();
            if (!name || name === '.' || name === '..' || name.includes('\\')) {
                throw new Error('Invalid upload filename. Contact your administrator.');
            }
            const directory = await localDirectory(root, parts.join('/'));
            let handle: FileSystemFileHandle;
            try {
                handle = await directory.getFileHandle(name, { create: true });
            } catch (error) {
                throw localNameError(entry.path, error);
            }
            const local = await handle.getFile();
            const checkpoint: FileCheckpoint | undefined = await readRequest(
                database
                    .transaction('files')
                    .objectStore('files')
                    .get([destination, source, entry.path]),
            );
            if (
                checkpoint?.version === entry.version &&
                checkpoint.size === local.size &&
                checkpoint.lastModified === local.lastModified &&
                local.size === entry.sizeBytes
            ) {
                progress.skipped++;
                progress.skippedBytes += entry.sizeBytes;
                progress.completedBytes += entry.sizeBytes;
                update();
                return null;
            }
            return { entry, handle };
        };
        const commit = async ({ entry, handle }: PreparedFile, received: number) => {
            const saved = await handle.getFile();
            await saveRecord(database, 'files', {
                destination,
                source,
                path: entry.path,
                version: entry.version,
                size: saved.size,
                lastModified: saved.lastModified,
            });
            progress.completedBytes += received;
            progress.downloaded++;
            update();
        };
        const downloadFile = async (file: PreparedFile) => {
            const { entry, handle } = file;
            const params = { path: entry.path, version: entry.version };
            const download = await request('file', params, controller.signal);
            if (!download.body) throw new Error('No content returned. Retry the transfer.');
            let writable: FileSystemWritableFileStream;
            try {
                writable = await handle.createWritable();
            } catch (error) {
                await download.body.cancel().catch((): void => {});
                throw error;
            }
            let received = 0;
            try {
                const stream = new TransformStream<Uint8Array, Uint8Array>({
                    transform(chunk, output) {
                        received += chunk.byteLength;
                        progress.bytes += chunk.byteLength;
                        progress.activeBytes += chunk.byteLength;
                        update();
                        output.enqueue(chunk);
                    },
                });
                await download.body.pipeThrough(stream).pipeTo(writable, {
                    preventClose: true,
                    signal: controller.signal,
                });
                if (received !== entry.sizeBytes)
                    throw new Error('Download incomplete. Retry the transfer.');
                await request('file', params, controller.signal, 'HEAD');
                controller.signal.throwIfAborted();
                await writable.close();
            } catch (error) {
                await writable.abort().catch((): void => {});
                throw error;
            } finally {
                progress.activeBytes -= received;
            }
            await commit(file, received);
        };
        const downloadBatch = async (files: PreparedFile[]) => {
            let index = 0;
            let received = 0;
            let writable: FileSystemWritableFileStream | null = null;
            let fileError: unknown;
            try {
                const response = await request(
                    'batch',
                    {
                        files: JSON.stringify({
                            files: files.map(({ entry }) => ({
                                path: entry.path,
                                version: entry.version,
                            })),
                        }),
                    },
                    controller.signal,
                    'POST',
                );
                for await (const record of responseRecords<BatchRecord>(
                    response,
                    controller.signal,
                )) {
                    if (record.index !== index || !files[index]) {
                        throw new Error('Invalid batch response. Retry the transfer.');
                    }
                    const file = files[index];
                    progress.currentPath = file.entry.path;
                    if (!writable && !fileError) {
                        try {
                            writable = await file.handle.createWritable();
                        } catch (error) {
                            fileError = error;
                        }
                    }
                    if (record.data) {
                        const bytes = Uint8Array.from(atob(record.data), character =>
                            character.charCodeAt(0),
                        );
                        received += bytes.byteLength;
                        progress.bytes += bytes.byteLength;
                        progress.activeBytes += bytes.byteLength;
                        if (!fileError && writable) {
                            try {
                                await writable.write(bytes);
                            } catch (error) {
                                fileError = error;
                            }
                        }
                        update();
                    }
                    if (record.complete) {
                        try {
                            if (record.error) throw new Error(record.error);
                            if (fileError) throw fileError;
                            if (!writable || received !== file.entry.sizeBytes) {
                                throw new Error('Download incomplete. Retry the transfer.');
                            }
                            controller.signal.throwIfAborted();
                            // The server verifies each source before sending its completion record.
                            await writable.close();
                            const savedBytes = received;
                            progress.activeBytes -= received;
                            received = 0;
                            await commit(file, savedBytes);
                        } catch (error) {
                            controller.signal.throwIfAborted();
                            markFailed(file.entry.path, file.entry.sizeBytes, error);
                            await writable?.abort().catch((): void => {});
                        }
                        progress.activeBytes -= received;
                        received = 0;
                        writable = null;
                        fileError = undefined;
                        index++;
                        update();
                    }
                }
                if (index !== files.length)
                    throw new Error('Batch download interrupted. Retry the transfer.');
            } catch (error) {
                controller.signal.throwIfAborted();
                for (const file of files.slice(index))
                    markFailed(file.entry.path, file.entry.sizeBytes, error);
            } finally {
                progress.activeBytes -= received;
                await writable?.abort().catch((): void => {});
            }
        };
        for await (const entries of pages()) {
            const jobs: UploadEntry[][] = [];
            let small: UploadEntry[] = [];
            let smallBytes = 0;
            for (const entry of entries) {
                if (entry.isDirectory) {
                    try {
                        await localDirectory(root, entry.path);
                    } catch (error) {
                        controller.signal.throwIfAborted();
                        markFailed(entry.path, 0, error);
                    }
                    continue;
                }
                if (entry.sizeBytes > 256 * 1024) {
                    jobs.push([entry]);
                    continue;
                }
                if (small.length === 100 || smallBytes + entry.sizeBytes > 8 * 1024 * 1024) {
                    jobs.push(small);
                    small = [];
                    smallBytes = 0;
                }
                small.push(entry);
                smallBytes += entry.sizeBytes;
            }
            if (small.length > 0) jobs.push(small);
            let index = 0;
            let failure: unknown;
            const worker = async () => {
                try {
                    while (index < jobs.length) {
                        controller.signal.throwIfAborted();
                        const job = jobs[index++];
                        const prepared: PreparedFile[] = [];
                        for (const entry of job) {
                            try {
                                const file = await prepare(entry);
                                if (file) prepared.push(file);
                            } catch (error) {
                                controller.signal.throwIfAborted();
                                markFailed(entry.path, entry.sizeBytes, error);
                            }
                        }
                        if (prepared.length === 0) continue;
                        if (prepared[0].entry.sizeBytes <= 256 * 1024)
                            await downloadBatch(prepared);
                        else {
                            const file = prepared[0];
                            try {
                                await downloadFile(file);
                            } catch (error) {
                                controller.signal.throwIfAborted();
                                markFailed(file.entry.path, file.entry.sizeBytes, error);
                            }
                        }
                    }
                } catch (error) {
                    failure ??= error;
                    controller.abort(error);
                }
            };
            await Promise.all([worker(), worker(), worker(), worker()]);
            if (failure) throw failure;
            controller.signal.throwIfAborted();
        }
        progress.phase = 'completed';
        // This is live storage: reconcile totals if files were added or removed during the scan.
        progress.totalFiles = progress.downloaded + progress.skipped + progress.failedFiles;
        progress.totalBytes = progress.completedBytes + progress.failedBytes;
        progress.currentPath = '';
        update(true);
    } finally {
        clearInterval(heartbeat);
        update(true, false);
        signal.removeEventListener('abort', cancel);
        try {
            await readRequest(
                database
                    .transaction('manifest', 'readwrite')
                    .objectStore('manifest')
                    .delete(IDBKeyRange.bound([scan, 0], [scan, Number.MAX_SAFE_INTEGER])),
            );
        } finally {
            database.close();
        }
    }
}
